import { createSelector } from '@reduxjs/toolkit';

import createRootSelector from 'common/apps/core/selectors/createRootSelector';

import {
  AffiliateUser,
  ControlUser,
  InterfaceViewType,
  NumericProject,
  User,
} from '../types/user';

import { AuthPartialState } from './reducers/auth';
import isUserAffiliate from './helpers/isUserAffiliate';
import isUserAccountant from './helpers/isUserAccountant';
import isUserAdmin from './helpers/isUserAdmin';
import isUserHeadManager from './helpers/isUserHeadManager';
import isUserStripcashManager from './helpers/isUserStripcashManager';
import isUserAnyManager from './helpers/isUserAnyManager';
import getStripchatScheme from './helpers/getStripchatScheme';
import isUserCustomManager from './helpers/isUserCustomManager';
import isUserLovescapeManager from './helpers/isUserLovescapeManager';
import isSchemePaid from './helpers/isSchemePaid';
import isUserTrialManager from './helpers/isUserTrialManager';
import currentProjectIdSelector from './selectors/currentProjectIdSelector';

export const authSelector = ({ auth }: AuthPartialState) => auth;

export const currentAffiliateUserRootSelector = createRootSelector(
  authSelector,
  ({ user }) => user as AffiliateUser,
);

export const currentControlUserRootSelector = createRootSelector(
  authSelector,
  ({ user }) => user as ControlUser,
);

export const currentUserMaybeSelector = <T = User>({
  auth,
}: AuthPartialState<T>) => auth?.user;

export const currentUserSelector = (state: AuthPartialState) =>
  currentUserMaybeSelector(state) || {};
export const isUserLoggedInSelector = (state: AuthPartialState) =>
  Boolean(currentUserMaybeSelector(state));

export const isUserApprovedSelector = createSelector(
  currentUserSelector,
  ({ interfaceView }) => interfaceView === InterfaceViewType.Full,
);
export const userTrackIdSelector = createSelector(
  currentUserSelector,
  ({ trackId }) => trackId,
);
export const userIdSelector = createSelector(
  currentUserSelector,
  ({ id }) => id,
);

export const isCurrentUserAdminSelector = createSelector(
  currentUserSelector,
  isUserAdmin,
);

export const isCurrentUserHeadManagerSelector = createSelector(
  currentUserSelector,
  isUserHeadManager,
);

export const isCurrentUserTrialManagerSelector = createSelector(
  currentUserSelector,
  isUserTrialManager,
);

export const isCurrentUserAccountantSelector = createSelector(
  currentUserSelector,
  isUserAccountant,
);

export const isCurrentUserSimpleManagerSelector = createSelector(
  currentUserSelector,
  isUserStripcashManager,
);

export const isCurrentUserManagerSelector = createSelector(
  currentUserSelector,
  isUserAnyManager,
);

export const isCurrentUserCustomManagerSelector = createSelector(
  currentUserSelector,
  isUserCustomManager,
);

export const isCurrentUserLovescapeManagerSelector = createSelector(
  currentUserSelector,
  isUserLovescapeManager,
);

export const isCurrentUserAffiliateSelector = createSelector(
  currentUserSelector,
  isUserAffiliate,
);

export const userStripchatSchemeSelector = createSelector(
  currentUserSelector,
  getStripchatScheme,
);

export const userSchemeTypeSelector = createSelector(
  userStripchatSchemeSelector,
  (scheme) => scheme?.schemeType,
);

export const isUserSchemePaidSelector = createSelector(
  userStripchatSchemeSelector,
  isSchemePaid,
);

export const isCurrentUserHasStripchatSchemeSelector = createSelector(
  currentProjectIdSelector,
  (currentProjectId) => currentProjectId === NumericProject.Stripchat,
);
